import { useState, useEffect, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import lprJourneyAPI from '../../../../services/lprJourneyAPI';
import CameraSetupModal from './CameraSetupModal';
import LprJourneyFeatureIntro, { LPR_JOURNEY_INTRO_SEEN_KEY } from './LprJourneyFeatureIntro';
import LprJourneyMain from './LprJourneyMain';
import './LprJourney.css';

/**
 * LprJourney — top-level suite component
 *
 * Props:
 *   selectedNetwork  {object|null}  - { network_id, network_name, ... }
 *   selectedBusiness {object|null}  - { token, ... }
 *   selectedYear     {string|null}
 */
const LprJourney = ({ selectedNetwork, selectedBusiness, selectedYear }) => {
  const [cameras, setCameras] = useState([]);
  const [config, setConfig] = useState(null);          // { cameras, hasBackgroundImage, isFullyConfigured }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showFeatureIntro, setShowFeatureIntro] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  const networkId = selectedNetwork?.network_id || null;
  const networkName = selectedNetwork?.network_name || '';

  // ─── Load cameras + config whenever network changes ───────────────────────
  const loadData = useCallback(async () => {
    if (!networkId) {
      setCameras([]);
      setConfig(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [fetchedCameras, fetchedConfig] = await Promise.all([
        lprJourneyAPI.getCamerasByNetworkId(networkId),
        lprJourneyAPI.getConfig(networkId)
      ]);

      setCameras(fetchedCameras);
      setConfig(fetchedConfig);

      // Auto-popup if cameras exist but not fully configured
      if (fetchedCameras.length > 0 && !fetchedConfig.isFullyConfigured) {
        setShowSetupModal(true);
      } else {
        setShowSetupModal(false);
      }
    } catch (err) {
      console.error('Error loading LPR Journey data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [networkId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Show feature intro on first visit (when network selected & loaded) ───
  useEffect(() => {
    if (!networkId || isLoading || error) return;
    try {
      const seen = localStorage.getItem(LPR_JOURNEY_INTRO_SEEN_KEY);
      if (!seen) {
        setIsFirstVisit(true);
        setShowFeatureIntro(true);
      }
    } catch { /* ignore */ }
  }, [networkId, isLoading, error]);

  // ─── After saving config, reload and close modal ──────────────────────────
  const handleSave = async () => {
    setShowSetupModal(false);
    await loadData();
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (!networkId) {
    return (
      <div className="lpr-journey-empty">
        <button
          className="lpr-journey-help-btn lpr-journey-help-btn--over-empty"
          onClick={() => {
            setIsFirstVisit(false);
            setShowFeatureIntro(true);
          }}
          title="What is LPR Journey?"
          aria-label="Learn about LPR Journey"
        >
          <HelpCircle size={18} />
          <span className="lpr-journey-help-btn-text">What is LPR Journey?</span>
        </button>
        <div className="lpr-journey-empty-content">
          <span className="lpr-journey-empty-icon">🗺️</span>
          <h3 className="lpr-journey-empty-title">Select a network to get started</h3>
          <p className="lpr-journey-empty-text">
            LPR Journey shows how vehicles move between your cameras. Choose a network from the
            filter above, then configure your map and view traffic flow &amp; plate journeys.
          </p>
        </div>
        <LprJourneyFeatureIntro
          show={showFeatureIntro}
          onClose={() => setShowFeatureIntro(false)}
          isFirstVisit={isFirstVisit}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="lpr-journey-loading">
        <FadeLoader color="#6366f1" />
        <p className="lpr-journey-loading-text">Loading LPR Journey data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lpr-journey-error">
        <span className="lpr-journey-error-icon">⚠️</span>
        <p className="lpr-journey-error-text">{error}</p>
        <button className="lpr-journey-retry-btn" onClick={loadData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="lpr-journey-container">
      {/* Main view is always rendered when a network is selected */}
      <LprJourneyMain
        config={config}
        cameras={cameras}
        networkId={networkId}
        networkName={networkName}
        onConfigureMap={() => setShowSetupModal(true)}
        onShowHelp={() => {
          setIsFirstVisit(false);
          setShowFeatureIntro(true);
        }}
      />

      {/* Feature intro modal — first visit or via help button */}
      <LprJourneyFeatureIntro
        show={showFeatureIntro}
        onClose={() => setShowFeatureIntro(false)}
        isFirstVisit={isFirstVisit}
      />

      {/* Setup modal — auto-pops when not fully configured, or opened manually */}
      {showSetupModal && (
        <CameraSetupModal
          networkId={networkId}
          cameras={cameras}
          existingConfig={config}
          onSave={handleSave}
          onClose={() => setShowSetupModal(false)}
          isRequired={false}
        />
      )}
    </div>
  );
};

export default LprJourney;
